import TextField from "@mui/material/TextField";
import React, { useCallback } from "react";

export const ONE_TIME_CODE_LENGTH = 6;

interface OneTimeCodeInputProps {
	value: string;
	onChange: (value: string) => void;
	// Fired once the user has entered (typed or pasted) all digits.
	onComplete: (code: string) => void;
	label: string;
	disabled?: boolean;
	error?: boolean;
	helperText?: string;
	autoFocus?: boolean;
	length?: number;
}

/**
 * Single-field one-time-code input. One field (rather than six boxes) keeps
 * paste, SMS autofill (`autocomplete="one-time-code"`) and screen readers
 * simple. Anything that is not a digit is dropped, so pasting
 * "Your code: 123 456" yields "123456". There is deliberately no
 * `maxLength`: the browser would truncate such a paste before we clean it.
 */
const OneTimeCodeInput = ({
	value,
	onChange,
	onComplete,
	label,
	disabled = false,
	error = false,
	helperText,
	autoFocus = false,
	length = ONE_TIME_CODE_LENGTH,
}: OneTimeCodeInputProps) => {
	const handleChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			const digits = event.target.value.replace(/\D/g, "").slice(0, length);
			onChange(digits);
			if (digits.length === length && digits !== value) {
				onComplete(digits);
			}
		},
		[length, onChange, onComplete, value]
	);

	return (
		<TextField
			id='one-time-code'
			label={label}
			value={value}
			onChange={handleChange}
			disabled={disabled}
			error={error}
			helperText={helperText}
			autoFocus={autoFocus}
			fullWidth
			variant='outlined'
			margin='normal'
			InputLabelProps={{ shrink: true }}
			inputProps={{
				inputMode: "numeric",
				autoComplete: "one-time-code",
				pattern: "[0-9]*",
				"data-testid": "one-time-code-input",
				style: {
					letterSpacing: "0.5em",
					fontSize: "22px",
					textAlign: "center",
				},
			}}
		/>
	);
};

export default OneTimeCodeInput;
